import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import YudielQRScanner from './YudielQRScanner';
import ManualQRInput from './ManualQRInput';
import QRTestHelper from './QRTestHelper';
import EventSelector from './EventSelector';
import { setEvents } from '../../store/slices/eventSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { adminApiService } from '../../services/adminApi';
import { requireAttendanceAccess } from '../common/SecureAdminPage';
import { parseQRCode } from '../../utils/qrEncryption';

const CheckInPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { events, loading } = useSelector(state => state.events);
  
  // Debug logging for Redux state
  console.log('🔍 CheckInPage: Redux events state:', events);
  console.log('🔍 CheckInPage: Events loading:', loading);
  console.log('🔍 CheckInPage: Events type:', typeof events);
  console.log('🔍 CheckInPage: Events is array:', Array.isArray(events));
  
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [keepScannerRunning, setKeepScannerRunning] = useState(false);
  const [scannerType, setScannerType] = useState('scanner'); // Default to Universal Scanner
  const [showDiagnostic, setShowDiagnostic] = useState(false);

  useEffect(() => {
    // Fetch all events when component mounts
    const fetchEventsWithSections = async () => {
      try {
        console.log('🔄 CheckInPage: Fetching events with sections...');
        
        // First get the list of events (using correct parameters)
        const eventsResponse = await adminApiService.events.getAll({ skip: 0, limit: 1000 });
        console.log('✅ CheckInPage: Events response:', eventsResponse);
        
        // Events API returns array directly, not wrapped in .data
        const eventsList = Array.isArray(eventsResponse) ? eventsResponse : (eventsResponse.data || []);
        console.log('✅ CheckInPage: Events list:', eventsList);
        
        // Then fetch each event with its sections
        const eventsWithSections = await Promise.all(
          eventsList.map(async (event) => {
            try {
              console.log('🔄 CheckInPage: Fetching details for event:', event.id);
              const eventResponse = await adminApiService.events.getById(event.id);
              console.log('✅ CheckInPage: Event details response:', eventResponse);
              
              // Handle response structure (might be direct data or wrapped)
              const eventData = eventResponse.data || eventResponse;
              return eventData;
            } catch (error) {
              console.error(`❌ CheckInPage: Failed to fetch details for event ${event.id}:`, error);
              return { ...event, sections: [] };
            }
          })
        );
        
        console.log('✅ CheckInPage: Final events with sections:', eventsWithSections);
        
        // Update the Redux store with the fetched events
        dispatch(setEvents(eventsWithSections)); // Remove extra wrapping
        
      } catch (error) {
        console.error('❌ CheckInPage: Failed to fetch events:', error);
        dispatch(addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to load events. Please refresh the page.',
        }));
      }
    };

    fetchEventsWithSections();
  }, [dispatch]);

  const handleEventChange = (event) => {
    console.log('🔄 CheckInPage: Event changed to:', event);
    setSelectedEvent(event);
    setSelectedSection(null); // Reset section when event changes
  };

  const handleSectionChange = (section) => {
    console.log('🔄 CheckInPage: Section changed to:', section);
    setSelectedSection(section);
  };

  const handleStartScanning = () => {
    if (!selectedEvent || !selectedSection) {
      dispatch(addNotification({
        type: 'warning',
        title: 'Selection Required',
        message: 'Please select both an event and a section before starting to scan.',
      }));
      return;
    }
    setIsScanning(true);
  };

  const handleStopScanning = () => {
    setIsScanning(false);
  };

  const handleScanResult = async (result) => {
    try {
      // Parse QR code result using the universal parser (handles both encrypted and JSON)
      let qrData;
      try {
        qrData = parseQRCode(result);
        console.log('✅ CheckInPage: Parsed QR data:', qrData);
      } catch (e) {
        throw new Error('Invalid QR code format. Please scan a valid attendance QR code.');
      }

      // worship_id is optional - can be missing, empty, or 0
      // mobile is also optional but should be passed if present
      // Add current datetime if not provided
      const currentDatetime = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 12);
      
      const checkInData = {
        worship_id: (qrData.worship_id !== null && qrData.worship_id !== undefined && String(qrData.worship_id).trim() !== '')
          ? String(qrData.worship_id).trim()
          : null,
        mobile: qrData.mobile || null,
        current_datetime: qrData.current_datetime || currentDatetime,
        event_id: selectedEvent.id,
        section_id: selectedSection.id
      };

      // Send to API
      try {
        console.log('🔄 CheckInPage: Sending check-in data:', checkInData);
        const response = await adminApiService.attendance.checkIn(checkInData);
        console.log('✅ CheckInPage: Check-in API response:', response);
        
        setScanResult({
          ...checkInData,
          event_name: selectedEvent.event_name,
          section_name: selectedSection.section_name,
          check_in_time: response.check_in_time || response.data?.check_in_time
        });

        // Check if this was an update (existing record) or new check-in
        const responseData = response.data || response;
        console.log('🔍 CheckInPage: Response data for notification:', responseData);
        // Fix the isUpdate logic - if updated_at is null, it's a new record
        const isUpdate = responseData.updated_at !== null;
        console.log('🔍 CheckInPage: Is update?', isUpdate, 'updated_at:', responseData.updated_at);
        
        // Build success message - handle cases where worship_id may be missing/null/0
        const worshipIdText = checkInData.worship_id ? `Worship ID ${checkInData.worship_id} ` : '';
        dispatch(addNotification({
          type: 'success',
          title: isUpdate ? 'Check-in Updated' : 'Check-in Successful',
          message: isUpdate 
            ? `${worshipIdText}check-in time updated successfully!`
            : `${worshipIdText}checked in successfully!`,
        }));
        console.log('✅ CheckInPage: Success notification dispatched');

        // Only stop scanning if "Keep Scanner Running" is not enabled
        if (!keepScannerRunning) {
          setIsScanning(false);
        }

      } catch (apiError) {
        console.error('❌ CheckInPage: API error:', apiError);
        throw new Error('Failed to submit check-in. Please try again.');
      }

    } catch (error) {
      console.error('❌ CheckInPage: General error:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Check-in Error',
        message: error.message,
      }));
    }
  };

  const handleClearResult = () => {
    setScanResult(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('attendance.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Security Notice */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-green-800 text-sm font-medium">
              🔒 {t('checkIn.secureNotice')}
            </span>
          </div>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('checkIn.checkInAttendance')}</h1>
          <p className="text-gray-600">
            {t('checkIn.scanInstructions')}
          </p>
        </div>

        {/* Event and Section Selection */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('checkIn.selectEventSection')}</h2>
          
          <EventSelector
            events={events || []}
            selectedEvent={selectedEvent}
            selectedSection={selectedSection}
            onEventChange={handleEventChange}
            onSectionChange={handleSectionChange}
          />

          {selectedEvent && selectedSection && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800">
                <strong>Selected:</strong> {selectedEvent.event_name} - {selectedSection.section_name}
                <br />
                <strong>Time:</strong> {new Date(selectedSection.start_at).toLocaleString()} - {new Date(selectedSection.end_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {/* QR Code Scanner Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('checkIn.qrCodeScanner')}</h2>
            <div className="flex items-center space-x-4">
              {!isScanning && (
                <>
                  <select 
                    value={scannerType} 
                    onChange={(e) => setScannerType(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-1"
                  >
                    <option value="scanner">{t('checkIn.scannerTypeUniversal')}</option>
                    <option value="manual">{t('checkIn.manualInput')}</option>
                  </select>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={keepScannerRunning}
                      onChange={(e) => setKeepScannerRunning(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Keep Scanner Running</span>
                  </label>
                  <button
                    onClick={handleStartScanning}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                    disabled={!selectedEvent || !selectedSection}
                  >
                    {t('checkIn.startScanning')}
                  </button>
                </>
              )}
              {isScanning && (
                <>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={keepScannerRunning}
                        onChange={(e) => setKeepScannerRunning(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Keep Scanner Running</span>
                    </label>
                    <button
                      onClick={handleStopScanning}
                      className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                    >
                      Stop Scanning
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {isScanning && scannerType === 'scanner' && (
            <YudielQRScanner
              onScanResult={handleScanResult}
              onError={(error) => {
                dispatch(addNotification({
                  type: 'error',
                  title: 'Scanner Error',
                  message: error.message,
                }));
              }}
            />
          )}

          {isScanning && scannerType === 'manual' && (
            <ManualQRInput
              onScanResult={handleScanResult}
              onError={(error) => {
                dispatch(addNotification({
                  type: 'error',
                  title: 'Input Error',
                  message: error.message,
                }));
              }}
            />
          )}

          {!isScanning && !scanResult && (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">📱</div>
              <p>Click "Start Scanning" to begin QR code scanning</p>
            </div>
          )}
        </div>

        {/* QR Test Helper for Debugging */}
        <div className="mb-6">
          <QRTestHelper />
        </div>

        {/* Scan Result Display */}
        {scanResult && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Last Scan Result</h2>
              <button
                onClick={handleClearResult}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded text-sm"
              >
                Clear
              </button>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(scanResult, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Export with security wrapper requiring attendance access
// TEMPORARILY DISABLED FOR DEBUGGING
export default CheckInPage;
// export default requireAttendanceAccess(CheckInPage);
